import React, { useEffect, useMemo, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import {
  Avatar, Box, Button, Chip, Divider, Grid, LinearProgress, List, ListItem,
  ListItemAvatar, ListItemText, Paper, Skeleton, Stack, Tooltip, Typography,
} from '@mui/material';
import {
  ArrowDownward, ArrowUpward, Assignment, FitnessCenter, Groups, People,
  Remove, Schedule, TrendingDown, TrendingUp, Wallet, InfoOutlined
} from '@mui/icons-material';
import {
  Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer,
  Tooltip as RechartTooltip, XAxis, YAxis, Cell, LineChart, Line
} from 'recharts';
import { dashboardAPI, usersAPI } from '../services/api';
import TopActiveUsersWidget from '../components/TopActiveUsersWidget';
import { useAppData } from '../context/AppDataContext';
import { maskPhone } from '../utils/masking';
import { GYM_MODULES, userCanAccessGymModule } from '../utils/gymModules';
import PageHeader from '../components/PageHeader';
import { BRAND, BLUE_GRADIENT } from '../theme/fitisticTokens';

const COLORS = {
  ink: BRAND.textDark,
  muted: BRAND.textMid,
  border: BRAND.divider,
  blue: BRAND.primary,
  green: BRAND.green,
  amber: BRAND.gold,
  violet: BRAND.purple,
  cyan: BRAND.teal,
  red: BRAND.red,
};

function getUser() {
  try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
}

function formatNumber(value) {
  if (typeof value !== 'number') return value ?? '-';
  return value.toLocaleString('en-IN');
}

function initials(name, email) {
  const source = (name || email || '?').trim();
  return source[0]?.toUpperCase() || '?';
}

function TrendBadge({ value }) {
  if (value == null) {
    return <Chip label="No baseline" size="small" sx={{ height: 22, fontSize: 11, bgcolor: 'action.hover', color: 'text.secondary' }} />;
  }

  const isUp = value > 0;
  const isFlat = value === 0;
  const Icon = isFlat ? Remove : isUp ? ArrowUpward : ArrowDownward;
  const color = isFlat ? COLORS.muted : isUp ? COLORS.green : COLORS.red;
  const bg = isFlat ? '#F3F4F6' : isUp ? '#F0FDF4' : '#FEF2F2';

  return (
    <Tooltip title="Compared with last month" arrow>
      <Chip
        icon={<Icon sx={{ fontSize: 13 }} />}
        label={isFlat ? '0%' : `${Math.abs(value)}%`}
        size="small"
        sx={{
          height: 22,
          fontSize: 11,
          fontWeight: 700,
          color,
          bgcolor: bg,
          '& .MuiChip-icon': { color, ml: 0.5 },
        }}
      />
    </Tooltip>
  );
}

function MetricTile({ title, value, caption, icon, color, trend, loading, onClick, tooltip }) {
  return (
    <Paper
      elevation={0}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(event) => {
        if (onClick && (event.key === 'Enter' || event.key === ' ')) onClick();
      }}
      sx={{
        p: 2.5,
        height: '100%',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
        boxShadow: '0px 1px 2px rgba(15, 23, 42, 0.04)',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': onClick ? {
          borderColor: color,
          boxShadow: '0px 10px 15px -3px rgba(15, 23, 42, 0.08), 0px 4px 6px -2px rgba(15, 23, 42, 0.04)',
          transform: 'translateY(-2px)'
        } : undefined,
        '&:focus-visible': {
          outline: '2px solid transparent',
          boxShadow: `0 0 0 2px #fff, 0 0 0 4px ${color}`,
        },
      }}
    >
      {onClick && (
        <Box sx={{
          position: 'absolute', top: 0, right: 0, width: 120, height: 120,
          background: `radial-gradient(circle, ${color}10 0%, transparent 70%)`,
          transform: 'translate(30%, -30%)',
          pointerEvents: 'none'
        }} />
      )}
      <Stack spacing={2}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Box sx={{
            width: 44, height: 44, borderRadius: 1.5,
            bgcolor: `${color}15`, display: 'grid', placeItems: 'center',
            color: color,
            boxShadow: `inset 0 0 0 1px ${color}20`
          }}>
            {React.cloneElement(icon, { sx: { fontSize: 24 } })}
          </Box>
          <TrendBadge value={trend} />
        </Stack>
        {loading ? (
          <Box>
            <Skeleton width={86} height={40} />
            <Skeleton width={120} height={20} sx={{ mt: 1 }} />
          </Box>
        ) : (
          <Box>
            <Typography sx={{ fontSize: 32, lineHeight: 1.1, fontWeight: 800, color: 'text.primary', letterSpacing: '-0.02em' }}>
              {formatNumber(value)}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 1 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary' }}>
                {title}
              </Typography>
              {tooltip && (
                <Tooltip title={tooltip} arrow placement="top">
                  <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              )}
            </Stack>
            {caption && (
              <Typography sx={{ mt: 0.5, fontSize: 13, color: 'text.secondary' }}>
                {caption}
              </Typography>
            )}
          </Box>
        )}
      </Stack>
    </Paper>
  );
}

function Panel({ title, subtitle, action, children, sx }) {
  return (
    <Paper elevation={0} sx={{ p: 2.25, border: 1, borderColor: 'divider', borderRadius: 1.5, height: '100%', ...sx }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography sx={{ fontSize: 15, fontWeight: 800, color: 'text.primary' }}>{title}</Typography>
          {subtitle && <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.25 }}>{subtitle}</Typography>}
        </Box>
        {action}
      </Stack>
      {children}
    </Paper>
  );
}

function ProgressRow({ label, value, color }) {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary' }}>{label}</Typography>
        <Typography sx={{ fontSize: 13, fontWeight: 800, color }}>{pct}%</Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{ height: 7, borderRadius: 999, bgcolor: 'action.hover', '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 999 } }}
      />
    </Box>
  );
}

function TodayAgenda({ items, loading, onOpen }) {
  return (
    <Panel title="Daily Tasks" subtitle="Schedule, appointments, and tasks">
      {loading ? (
        <Stack spacing={2}>
          {[...Array(4)].map((_, index) => (
            <Stack key={index} direction="row" spacing={1.5} alignItems="flex-start">
              <Skeleton width={44} height={20} />
              <Skeleton variant="circular" width={10} height={10} sx={{ mt: 0.75 }} />
              <Box sx={{ flex: 1 }}>
                <Skeleton width="55%" height={20} />
                <Skeleton width="80%" height={17} />
              </Box>
            </Stack>
          ))}
        </Stack>
      ) : items.length === 0 ? (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Schedule sx={{ color: 'text.disabled', fontSize: 38, mb: 1 }} />
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>No agenda items for today</Typography>
        </Box>
      ) : (
        <Stack spacing={2}>
          {items.map((item, index) => (
            <Box
              key={`${item.type || 'item'}-${item.time}-${index}`}
              role={item.path ? 'button' : undefined}
              tabIndex={item.path ? 0 : undefined}
              onClick={() => item.path && onOpen(item.path)}
              onKeyDown={(event) => {
                if (item.path && (event.key === 'Enter' || event.key === ' ')) onOpen(item.path);
              }}
              sx={{
                display: 'grid',
                gridTemplateColumns: '48px 12px 1fr',
                gap: 1.25,
                alignItems: 'flex-start',
                cursor: item.path ? 'pointer' : 'default',
                borderRadius: 1,
                p: 0.5,
                mx: -0.5,
                '&:hover': item.path ? { bgcolor: 'action.hover' } : undefined,
                '&:focus-visible': {
                  outline: '3px solid rgba(37,99,235,0.24)',
                  outlineOffset: 2,
                },
              }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 800, color: 'text.primary', lineHeight: 1.55 }}>
                {item.time || '--:--'}
              </Typography>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#16172A', mt: 0.65 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 900, color: 'text.primary', lineHeight: 1.35 }} noWrap>
                  {item.title || 'Agenda item'}
                </Typography>
                <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.45 }}>
                  {item.note || 'Review this workflow'}
                </Typography>
              </Box>
            </Box>
          ))}
        </Stack>
      )}
    </Panel>
  );
}

export default function Dashboard() {
  const theme = useTheme();
  const navigate = useNavigate();
  const user = getUser();
  const { gyms: cachedGyms } = useAppData();
  const [stats, setStats] = useState(null);
  const [trends, setTrends] = useState({});
  const [recentMembers, setRecentMembers] = useState([]);
  const [todayItems, setTodayItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAll = async () => {
    setLoading(true);
    try {
      const [dashRes, todayRes, membersRes, advancedRes] = await Promise.all([
        dashboardAPI.getStats(),
        dashboardAPI.getToday(),
        usersAPI.getAll({ limit: 6, role: 'user' }).catch(() => ({ data: { data: [] } })),
        dashboardAPI.getAdvancedStats().catch(() => ({ data: { data: { mrr: 0, churnRate: 0, leadConversionRate: 0 } } })),
      ]);

      const data = dashRes.data.data || {};
      setStats({
        totalUsers: data.totalUsers || 0,
        totalGyms: data.totalGyms || 0,
        totalSessions: data.totalSessions || 0,
        activeMembers: data.activeMembers || 0,
        totalAttendance: data.totalAttendance || 0,
        todayAttendance: data.todayAttendance || 0,
        todaySessions: data.todaySessions || 0,
        totalChallenges: data.totalChallenges || 0,
        activeChallenges: data.activeChallenges || 0,
        upcomingEvents: data.upcomingEvents || 0,
        totalChallengeEnrollments: data.totalChallengeEnrollments || 0,
        thisMonth: data.thisMonth || {},
        lastMonth: data.lastMonth || {},
        advanced: advancedRes?.data?.data || { mrr: 0, churnRate: 0, leadConversionRate: 0 },
      });
      setTrends(data.trends || {});
      const membersData = membersRes?.data?.data || membersRes?.data?.users || [];
      setRecentMembers(Array.isArray(membersData) ? membersData : []);
      setTodayItems(todayRes.data.data || []);
    } catch (error) {
      console.error('Dashboard load error:', error);
      try {
        const membersRes = await usersAPI.getAll({ limit: 6, role: 'user' });
        const membersData = membersRes?.data?.data || membersRes?.data?.users || [];
        setRecentMembers(Array.isArray(membersData) ? membersData : []);
      } catch {}
    } finally {
      setLoading(false);
    }
  };

  const currentUserGymId = user?.gymId?._id || user?.gymId;
  const visibleGyms = currentUserGymId
    ? cachedGyms.filter(gym => String(gym._id) === String(currentUserGymId))
    : cachedGyms;

  const performanceData = [
    { name: 'New Users', thisMonth: stats?.thisMonth?.newUsers || 0, lastMonth: stats?.lastMonth?.newUsers || 0 },
    { name: 'Active Mems', thisMonth: stats?.thisMonth?.activeMems || 0, lastMonth: stats?.lastMonth?.activeMems || 0 },
    { name: 'Sessions', thisMonth: stats?.thisMonth?.sessions || 0, lastMonth: stats?.lastMonth?.sessions || 0 },
    { name: 'Attendance', thisMonth: stats?.thisMonth?.attendance || 0, lastMonth: stats?.lastMonth?.attendance || 0 },
    { name: 'Enrollments', thisMonth: stats?.thisMonth?.challengeEnrollments || 0, lastMonth: stats?.lastMonth?.challengeEnrollments || 0 },
  ];

  const health = useMemo(() => {
    const totalUsers = stats?.totalUsers || 0;
    const activeMembers = stats?.activeMembers || 0;
    const totalAttendance = stats?.totalAttendance || 0;
    const totalSessions = stats?.totalSessions || 0;

    return {
      activation: totalUsers ? Math.round((activeMembers / totalUsers) * 100) : 0,
      attendance: totalUsers ? Math.min(100, Math.round((totalAttendance / Math.max(totalUsers, 1)) * 10)) : 0,
      training: totalUsers ? Math.min(100, Math.round((totalSessions / Math.max(totalUsers, 1)) * 10)) : 0,
    };
  }, [stats]);

  const quickActions = [
    { label: 'Members', caption: 'Manage accounts', path: '/dashboard/users', icon: <People />, color: COLORS.blue },
    (user?.role === 'superadmin' || user?.role === 'gym_admin') ? { 
      label: user?.role === 'superadmin' ? 'Gyms' : 'My Gym', 
      caption: 'Locations and modules', 
      path: user?.role === 'superadmin' ? '/dashboard/gyms' : `/dashboard/gyms/${currentUserGymId}`, 
      icon: <FitnessCenter />, 
      color: COLORS.green 
    } : null,
    { label: 'Invoices', caption: 'Billing workflow', path: '/dashboard/invoices', icon: <Wallet />, color: COLORS.amber, module: GYM_MODULES.invoice },
    { label: 'Slots', caption: 'Booking calendar', path: '/dashboard/slot-management', icon: <Schedule />, color: COLORS.cyan, module: GYM_MODULES.slotBooking },
    { label: 'Retention', caption: 'Member risk', path: '/dashboard/retention', icon: <TrendingDown />, color: COLORS.red, module: GYM_MODULES.memberRetention },
  ].filter(Boolean).filter(action => userCanAccessGymModule(user, cachedGyms, action.module));
  const canViewChallengeMetrics = ['superadmin', 'gym_admin', 'admin'].includes(user?.role);

  return (
    <Box>
      <PageHeader
        title="Operations Dashboard"
        subtitle="User trends, operational metrics, and daily agenda."
        gradient={BLUE_GRADIENT}
        meta={user?.role === 'superadmin' && <Chip label="Platform view" size="small" sx={{ fontWeight: 700, height: 20, fontSize: '0.7rem' }} />}
        actions={<Button variant="outlined" size="small" onClick={loadAll}>Refresh</Button>}
      />

      <Grid container spacing={2}>
          <Grid item xs={12} sm={6} lg={3}>
            <MetricTile 
              title="Revenue Collected" 
              value={`₹${(stats?.revenueCollectedThisMonth || 0).toLocaleString('en-IN')}`} 
              caption={`Expected: ₹${(stats?.revenueExpectedThisMonth || 0).toLocaleString('en-IN')}`} 
              icon={<Wallet />} 
              color={COLORS.green} 
              trend={trends.revenue} 
              loading={loading} 
              onClick={() => navigate('/dashboard/invoices')}
              tooltip="Total membership fee revenue collected this month."
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <MetricTile 
              title="Outstanding Dues" 
              value={`₹${(stats?.outstandingDues || 0).toLocaleString('en-IN')}`} 
              caption="Unpaid member invoices" 
              icon={<TrendingDown />} 
              color={COLORS.red} 
              trend={null} 
              loading={loading} 
              onClick={() => navigate('/dashboard/invoices')}
              tooltip="Total pending dues from members with unpaid or overdue invoices."
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <MetricTile 
              title="Today's Attendance" 
              value={stats?.todayAttendance ?? 0} 
              caption="Check-ins today" 
              icon={<InfoOutlined />} 
              color={COLORS.amber} 
              trend={null} 
              loading={loading} 
              tooltip="The number of members who have checked into the gym today."
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <MetricTile 
              title="Total Members" 
              value={stats?.activeMembers} 
              caption={`Churn Rate: ${stats?.churnRate || 0}%`} 
              icon={<People />} 
              color={COLORS.blue} 
              trend={trends.users} 
              loading={loading} 
              onClick={() => navigate('/dashboard/users')}
              tooltip="The total active members and recent 60-day churn rate percentage."
            />
          </Grid>
        <Grid item xs={12} lg={8}>
          <Panel title="Monthly Trends" subtitle="Performance comparison (This Month vs Last Month)">
            {performanceData.every(d => d.thisMonth === 0 && d.lastMonth === 0) ? (
              <Box sx={{ py: 8, textAlign: 'center' }}>
                <TrendingUp sx={{ color: 'text.disabled', fontSize: 38, mb: 1 }} />
                <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>No trend data available</Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={310}>
                <BarChart data={performanceData} barSize={30} margin={{ top: 10, right: 12, left: -12, bottom: 0 }}>
                  <CartesianGrid stroke={theme.palette.divider} vertical={false} strokeDasharray="4 4" />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: theme.palette.text.secondary, fontWeight: 500 }} tickLine={false} axisLine={{ stroke: theme.palette.divider }} dy={8} />
                  <YAxis tick={{ fontSize: 12, fill: theme.palette.text.secondary, fontWeight: 500 }} tickLine={false} axisLine={false} width={40} />
                  <Legend wrapperStyle={{ fontSize: '13px', paddingTop: '10px' }} iconType="circle" />
                  <RechartTooltip
                    cursor={{ fill: theme.palette.action.hover }}
                    contentStyle={{ border: '1px solid', borderColor: theme.palette.divider, borderRadius: 8, boxShadow: '0 10px 15px -3px rgba(15,23,42,0.1)', padding: '12px 16px', fontWeight: 500, backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary }}
                    itemStyle={{ fontSize: 13 }}
                    labelStyle={{ fontSize: 14, fontWeight: 700, color: 'text.primary', marginBottom: 8 }}
                  />
                  <Bar dataKey="lastMonth" name="Last Month" fill={COLORS.muted} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="thisMonth" name="This Month" fill={COLORS.blue} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Panel>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Panel title="Operational Health" subtitle="Weighted signals from usage data">
            <Stack spacing={2.5}>
              <ProgressRow label="Member activation" value={health.activation} color={COLORS.green} />
              <ProgressRow label="Attendance coverage" value={health.attendance} color={COLORS.blue} />
              <ProgressRow label="Training engagement" value={health.training} color={COLORS.violet} />
              <Divider />
              <Stack direction="row" spacing={1.5}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 22, fontWeight: 800, color: 'text.primary' }}>{formatNumber(stats?.thisMonth?.attendance || 0)}</Typography>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Check-ins this month</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 22, fontWeight: 800, color: 'text.primary' }}>{formatNumber(stats?.thisMonth?.activeMems || 0)}</Typography>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Active members</Typography>
                </Box>
              </Stack>
            </Stack>
          </Panel>
        </Grid>

        <Grid item xs={12} lg={4}>
          <TodayAgenda items={todayItems} loading={loading} onOpen={navigate} />
        </Grid>

        <Grid item xs={12} lg={4}>
          <Panel title="Recent Members" subtitle="Latest user signups" action={<Button size="small" onClick={() => navigate('/dashboard/users')}>View All</Button>}>
            {loading ? (
              <Stack spacing={1.5}>
                {[...Array(5)].map((_, index) => (
                  <Stack key={index} direction="row" spacing={1.5} alignItems="center">
                    <Skeleton variant="circular" width={34} height={34} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton width="58%" height={16} />
                      <Skeleton width="42%" height={14} />
                    </Box>
                  </Stack>
                ))}
              </Stack>
            ) : recentMembers.length === 0 ? (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <People sx={{ color: 'text.disabled', fontSize: 38, mb: 1 }} />
                <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>No members found</Typography>
              </Box>
            ) : (
              <List disablePadding>
                {recentMembers.map((member, index) => (
                  <React.Fragment key={member._id || member.email}>
                    <ListItem disablePadding sx={{ py: 1 }}>
                      <ListItemAvatar sx={{ minWidth: 44 }}>
                        <Avatar sx={{ width: 34, height: 34, fontSize: 13, fontWeight: 800, bgcolor: index % 2 ? COLORS.cyan : COLORS.violet }}>
                          {initials(member.name, member.email)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={<Typography sx={{ fontSize: 13, fontWeight: 800 }} noWrap>{member.name || 'Unnamed member'}</Typography>}
                        secondary={<Typography sx={{ fontSize: 12, color: 'text.secondary' }} noWrap>{(member.phone ? maskPhone(member.phone) : member.email)}</Typography>}
                      />
                      <Chip
                        label={member.membershipStatus || 'active'}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: 11,
                          fontWeight: 700,
                          bgcolor: member.membershipStatus === 'active' ? '#F0FDF4' : '#F3F4F6',
                          color: member.membershipStatus === 'active' ? COLORS.green : COLORS.muted,
                          textTransform: 'capitalize'
                        }}
                      />
                    </ListItem>
                    {index < recentMembers.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Panel>
        </Grid>

        <Grid item xs={12} lg={4}>
          <TopActiveUsersWidget days={30} limit={7} />
        </Grid>

        <Grid item xs={12}>
          <Panel title="Quick Actions" subtitle="Common operational tasks">
            <Grid container spacing={1.5}>
              {quickActions.map(action => (
                <Grid item xs={12} sm={6} md={true} key={action.label}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => navigate(action.path)}
                    startIcon={React.cloneElement(action.icon, { sx: { color: action.color } })}
                    sx={{
                      justifyContent: 'flex-start',
                      textAlign: 'left',
                      py: 1.5,
                      px: 2,
                      borderColor: COLORS.border,
                      color: 'text.primary',
                      '&:hover': { borderColor: action.color, bgcolor: `${action.color}08` },
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 800, lineHeight: 1.25 }}>{action.label}</Typography>
                      <Typography sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.25, mt: 0.25 }}>{action.caption}</Typography>
                    </Box>
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Panel>
        </Grid>
      </Grid>
    </Box>
  );
}
